import React, { useEffect, useState } from 'react';
import useApi from '../CustomHooks/useApi';
import { userAPI } from '../../api/apiClient';
import { useToast } from '../../Contexts/ToastContext';
import {
  FaUserShield,
  FaUser,
  FaUserSlash,
  FaUserPlus,
  FaSpinner,
  FaSortAlphaDown,
  FaStream,
  FaSearch,
  FaUsers
} from 'react-icons/fa';
import ConfirmWithPhrasePopup from "../Common/ConfirmWithPhrasePopup";
import Loading from '../../Loading';
import AddUserPopup from "./AddUserPopup";
import ManageRolesPopup from "./ManageRolesPopup";
import DeletedUsersList from "./DeletedUsersList";
import "../../css/Users/UsersManager.css";

const UsersManager = () => {
  const { callApi: fetchUsersApi, isLoading: isLoadingUsers } = useApi();
  const { callApi: deleteUserApi, isLoading: isDeletingUser } = useApi();
  const { callApi: reactivateUserApi, isLoading: isReactivatingUser } = useApi();
  const { showToast, clearToast } = useToast();

  const [users, setUsers] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [groupByRoles, setGroupByRoles] = useState(false);
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [showManageRolesPopup, setShowManageRolesPopup] = useState(false);
  const [userToManageRoles, setUserToManageRoles] = useState(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const activeUsers = users.filter(u => u.isActive);
  const deletedUsers = users.filter(u => !u.isActive);

  const filteredUsers = activeUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDeletedUsers = deletedUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    const data = await fetchUsersApi(() => userAPI.getUsers());
    if (data) setUsers([...data].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const groupUsers = (userList) => {
    const grouped = {};
    userList.forEach((user) => {
      user.roles.forEach((role) => {
        if (!grouped[role.name]) grouped[role.name] = [];
        grouped[role.name].push(user);
      });
    });
    return grouped;
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const getInitials = (name = '') => name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || '?';

  const getRoleBadgeClass = (role) => {
    switch (Number(role)) {
      case 2: return 'usersmanager-role-badge--coordinator';
      case 3: return 'usersmanager-role-badge--board';
      case 5: return 'usersmanager-role-badge--mentor';
      case 6: return 'usersmanager-role-badge--finance';
      case 8: return 'usersmanager-role-badge--solidarity';
      case 9: return 'usersmanager-role-badge--social-media';
      default: return 'usersmanager-role-badge--default';
    }
  };

  const renderRoleBadge = (role) => (
    <span key={role.role} className={`usersmanager-role-badge ${getRoleBadgeClass(role.role)}`}>{role.name}</span>
  );

  const handleDeleteUser = (userGuid) => setUserToDelete(userGuid);

  const confirmDeleteUser = async () => {
    const user = users.find(u => u.guid === userToDelete);
    if (!user) { setUserToDelete(null); return; }
    showToast(`Usuwanie użytkownix ${user.name}...`, 'processing');
    setUserToDelete(null);
    await deleteUserApi(
      () => userAPI.deleteUser(user.guid),
      async () => { showToast('Użytkownix zostałx usuniętx.', 'success'); await fetchUsers(); },
      async () => { clearToast(); }
    );
  };

  const handleReactivateUser = async (userGuid) => {
    const user = users.find(u => u.guid === userGuid);
    if (!user) return;
    showToast(`Przywracanie użytkownix ${user.name}...`, 'processing');
    await reactivateUserApi(
      () => userAPI.reactivateUser(userGuid),
      async () => { showToast('Użytkownix zostałx przywrócony.', 'success'); await fetchUsers(); },
      async () => { clearToast(); }
    );
  };

  const toggleExpanded = (userGuid) => setExpandedUser(expandedUser === userGuid ? null : userGuid);

  const handleManageRolesClick = (user) => {
    setUserToManageRoles(user);
    setShowManageRolesPopup(true);
  };

  const renderUserCard = (user) => (
    <div key={user.guid} className="usersmanager-user-card" onClick={() => toggleExpanded(user.guid)}>
      <div className="usersmanager-user-main">
        <div className="usersmanager-avatar" aria-hidden="true">{getInitials(user.name)}</div>
        <div className="usersmanager-user-copy">
          <span className="usersmanager-user-name"><FaUser className="usersmanager-mobile-user-icon" />{user.name}</span>
          <span className="usersmanager-user-desktop-email">{user.email}</span>
          <div className="usersmanager-user-desktop-roles">
            {user.roles.slice().sort((a, b) => a.name.localeCompare(b.name)).map(renderRoleBadge)}
          </div>
        </div>
        <div className="usersmanager-user-actions">
          <button type="button" className="usersmanager-action-icon" title="Zarządzaj rolami" onClick={(e) => { e.stopPropagation(); handleManageRolesClick(user); }}><FaUserShield /></button>
          <button type="button" className="usersmanager-action-icon usersmanager-action-icon--danger" title="Usuń użytkownika" onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.guid); }}>
            {isDeletingUser && userToDelete === user.guid ? <FaSpinner className="fa-spin" /> : <FaUserSlash />}
          </button>
        </div>
      </div>
      {expandedUser === user.guid && (
        <div className="usersmanager-user-expanded">
          <div className="usersmanager-user-email">{user.email}</div>
          <div className="usersmanager-user-created">Dodano: {formatDate(user.createdAt)}</div>
          <div className="usersmanager-user-roles-badges">
            {user.roles.slice().sort((a, b) => a.name.localeCompare(b.name)).map(renderRoleBadge)}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="usersmanager-container">
      <div className="usersmanager-header">
        <div className="usersmanager-heading-copy">
          <h1 className="usersmanager-title">Użytkownicy</h1>
          <p className="usersmanager-subtitle">Zarządzaj użytkownikami systemu i ich rolami.</p>
        </div>
        <div className="usersmanager-actions">
          <button type="button" className="usersmanager-toolbar-button usersmanager-toolbar-button--primary" onClick={() => setShowAddUserPopup(true)} disabled={showDeletedUsers}><FaUserPlus /><span>Dodaj użytkownika</span></button>
          <button type="button" className={`usersmanager-toolbar-button ${groupByRoles ? 'usersmanager-toolbar-button--active' : ''}`} onClick={() => setGroupByRoles(!groupByRoles)} disabled={showDeletedUsers}>{groupByRoles ? <FaSortAlphaDown /> : <FaStream />}<span>{groupByRoles ? "Lista A-Z" : "Grupuj w role"}</span></button>
          <button type="button" className={`usersmanager-toolbar-button ${showDeletedUsers ? 'usersmanager-toolbar-button--active' : ''}`} onClick={() => { setShowDeletedUsers(!showDeletedUsers); setGroupByRoles(false); }}>{showDeletedUsers ? <FaUser /> : <FaUserSlash />}<span>{showDeletedUsers ? "Osoby aktywne" : "Osoby usunięte"}</span></button>
        </div>
      </div>

      <div className="usersmanager-toolbar-row">
        <label className="usersmanager-searchbar"><FaSearch /><input type="text" placeholder="Szukaj po imieniu, nazwisku lub e-mailu..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></label>
        <div className="usersmanager-summary-card"><span className="usersmanager-summary-icon"><FaUsers /></span><span><strong>{showDeletedUsers ? deletedUsers.length : activeUsers.length} użytkowników</strong><small>{showDeletedUsers ? 'usuniętych' : 'w systemie'}</small></span></div>
      </div>

      {isLoadingUsers ? (
        <Loading text="Ładowanie użytkowników..." />
      ) : showDeletedUsers ? (
        <DeletedUsersList deletedUsers={filteredDeletedUsers} onReactivate={handleReactivateUser} isReactivating={isReactivatingUser} />
      ) : (
        <div className="usersmanager-userlist">
          {!groupByRoles && <div className="activeusers-header"><span>Aktywne osoby</span><span className="usersmanager-section-count">{filteredUsers.length}</span></div>}
          {groupByRoles ? (
            Object.entries(groupUsers(filteredUsers)).map(([roleName, usersInRole]) => (
              <div key={roleName} className="usersmanager-role-group">
                <div className="usersmanager-role-title"><span>{roleName}</span><span className="usersmanager-section-count">{usersInRole.length}</span></div>
                {usersInRole.map(renderUserCard)}
              </div>
            ))
          ) : filteredUsers.map(renderUserCard)}
        </div>
      )}

      {userToDelete && <ConfirmWithPhrasePopup text={<>Czy na pewno chcesz usunąć użytkownix <span style={{ fontWeight: "bold" }}><FaUser /> {users.find(u => u.guid === userToDelete)?.name}</span> ?</>} phrase="okoń" onConfirm={confirmDeleteUser} onCancel={() => setUserToDelete(null)} />}
      {showAddUserPopup && <AddUserPopup onClose={() => setShowAddUserPopup(false)} onUserAdded={fetchUsers} />}
      {showManageRolesPopup && userToManageRoles && <ManageRolesPopup user={userToManageRoles} onClose={() => setShowManageRolesPopup(false)} onRolesUpdated={fetchUsers} />}
    </div>
  );
};

export default UsersManager;
